import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  EvStation as ChargerIcon,
  Power as PowerIcon,
  Bolt as BoltIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp,
  Visibility,
  Edit,
  Close,
  Save,
  Add,
} from '@mui/icons-material';

const ChargerType = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    connector: '',
    searchTerm: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusConfirmDialogOpen, setStatusConfirmDialogOpen] = useState(false);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [selectedForStatus, setSelectedForStatus] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editFormData, setEditFormData] = useState({
    typeName: '',
    connectorType: 'Type 2',
    brand: '',
    model: '',
    maxPowerKw: '',
    voltageV: '',
    currentA: '',
    isActive: true,
    description: '',
  });

  const sampleData = [
    {
      id: 1,
      typeName: 'AC Charger',
      connectorType: 'Type 2',
      brand: 'ABB',
      model: 'Terra AC',
      maxPowerKw: 22,
      voltageV: 400,
      currentA: 32,
      isActive: true,
      description: 'Wallbox AC charger suitable for residential and commercial use',
      efficiency: 96.5,
    },
    {
      id: 2,
      typeName: 'DC Fast Charger',
      connectorType: 'CCS2',
      brand: 'Delta',
      model: 'UltraFast 150',
      maxPowerKw: 150,
      voltageV: 800,
      currentA: 200,
      isActive: true,
      description: 'High power DC charger for highways and hubs',
      efficiency: 94.0,
    },
    {
      id: 3,
      typeName: 'DC Fast Charger',
      connectorType: 'CHAdeMO',
      brand: 'Tritium',
      model: 'Veefil-RT 50kW',
      maxPowerKw: 50,
      voltageV: 500,
      currentA: 125,
      isActive: true,
      description: 'Compact DC fast charger supporting CHAdeMO/CCS',
      efficiency: 93.5,
    },
    {
      id: 4,
      typeName: 'AC Charger',
      connectorType: 'Type 2',
      brand: 'Siemens',
      model: 'VersiCharge',
      maxPowerKw: 11,
      voltageV: 230,
      currentA: 16,
      isActive: false,
      description: 'Robust AC charger for indoor/outdoor installs',
      efficiency: 95.2,
    },
  ];

  const [data, setData] = useState(sampleData);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    if (selectedCharger) {
      handleUpdateChargerType();
    } else {
      handleCreateChargerType();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  const handleNewChargerType = () => {
    setEditFormData({
      typeName: '',
      connectorType: 'Type 2',
      brand: '',
      model: '',
      maxPowerKw: '',
      voltageV: '',
      currentA: '',
      isActive: true,
      description: '',
    });
    setSelectedCharger(null);
    setEditDialogOpen(true);
  };

  const handleCreateChargerType = () => {
    if (!editFormData.typeName || !editFormData.connectorType || !editFormData.maxPowerKw) {
      alert('Please fill in required fields: Type, Connector, Max Power');
      return;
    }

    const newItem = {
      id: Date.now(),
      typeName: editFormData.typeName,
      connectorType: editFormData.connectorType,
      brand: editFormData.brand,
      model: editFormData.model,
      maxPowerKw: parseFloat(editFormData.maxPowerKw),
      voltageV: parseFloat(editFormData.voltageV) || 0,
      currentA: parseFloat(editFormData.currentA) || 0,
      isActive: editFormData.isActive,
      description: editFormData.description,
      efficiency: 0,
    };

    console.log('Creating charger type:', newItem);
    setData(prev => [newItem, ...prev]);
    alert(`New charger type "${newItem.typeName}" created successfully!`);
    setEditDialogOpen(false);
    setSelectedCharger(null);
    setEditFormData({
      typeName: '',
      connectorType: 'Type 2',
      brand: '',
      model: '',
      maxPowerKw: '',
      voltageV: '',
      currentA: '',
      isActive: true,
      description: '',
    });
  };

  const handleUpdateChargerType = () => {
    if (!editFormData.typeName || !editFormData.connectorType || !editFormData.maxPowerKw) {
      alert('Please fill in required fields: Type, Connector, Max Power');
      return;
    }

    const updated = {
      ...selectedCharger,
      typeName: editFormData.typeName,
      connectorType: editFormData.connectorType,
      brand: editFormData.brand,
      model: editFormData.model,
      maxPowerKw: parseFloat(editFormData.maxPowerKw),
      voltageV: parseFloat(editFormData.voltageV) || 0,
      currentA: parseFloat(editFormData.currentA) || 0,
      isActive: editFormData.isActive,
      description: editFormData.description,
    };

    console.log('Updating charger type:', updated);
    setData(prev => prev.map(i => i.id === updated.id ? updated : i));
    alert(`Charger type "${updated.typeName}" updated successfully!`);
    setEditDialogOpen(false);
    setSelectedCharger(null);
    setEditFormData({
      typeName: '',
      connectorType: 'Type 2',
      brand: '',
      model: '',
      maxPowerKw: '',
      voltageV: '',
      currentA: '',
      isActive: true,
      description: '',
    });
  };

  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm }));
      setSearchLoading(false);
    }, 500);
  };

  const handleView = (item) => {
    setSelectedCharger(item);
    setViewDialogOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedCharger(item);
    setEditFormData({
      typeName: item.typeName,
      connectorType: item.connectorType,
      brand: item.brand,
      model: item.model,
      maxPowerKw: item.maxPowerKw.toString(),
      voltageV: item.voltageV.toString(),
      currentA: item.currentA.toString(),
      isActive: item.isActive,
      description: item.description,
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedCharger(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCharger(null);
    setEditFormData({
      typeName: '',
      connectorType: 'Type 2',
      brand: '',
      model: '',
      maxPowerKw: '',
      voltageV: '',
      currentA: '',
      isActive: true,
      description: '',
    });
  };

  const getFilteredData = useCallback(() => {
    if (!hasSearched) return data;
    let filtered = [...data];

    if (filters.searchTerm) {
      const q = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.typeName.toLowerCase().includes(q) ||
        item.brand.toLowerCase().includes(q) ||
        item.model.toLowerCase().includes(q) ||
        item.connectorType.toLowerCase().includes(q)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(item => filters.status === 'active' ? item.isActive : !item.isActive);
    }

    if (filters.connector) {
      filtered = filtered.filter(item => item.connectorType === filters.connector);
    }

    return filtered;
  }, [hasSearched, filters.searchTerm, filters.status, filters.connector, data]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalTypes = filteredData.length;
  const activeTypes = filteredData.filter(i => i.isActive).length;
  const totalMaxPower = filteredData.reduce((sum, i) => sum + i.maxPowerKw, 0);
  const averageEfficiency = filteredData.length > 0 ?
    (filteredData.reduce((sum, i) => sum + (i.efficiency || 0), 0) / filteredData.length).toFixed(1) : 0;

  const getStatusColor = (isActive) => isActive ? 'success' : 'error';

  const handleStatusToggle = (item) => {
    setSelectedForStatus(item);
    setStatusConfirmDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    if (!selectedForStatus) return;
    setData(prev => prev.map(i => i.id === selectedForStatus.id ? { ...i, isActive: !i.isActive } : i));
    setStatusConfirmDialogOpen(false);
    setSelectedForStatus(null);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Charger Type Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage EV charger types, connectors and power ratings
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleNewChargerType}
            sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            New Charger Type
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <ChargerIcon sx={{ fontSize: 28, color: '#536DFE', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {totalTypes}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Types
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {activeTypes}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Active Types
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <PowerIcon sx={{ fontSize: 28, color: '#ff9800', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {totalMaxPower} kW
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Max Power
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <TrendingUp sx={{ fontSize: 28, color: '#2196f3', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {averageEfficiency}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Avg Efficiency
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
            Search & Filters
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search charger type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(); }}
              sx={{ minWidth: 300, maxWidth: 400, flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select 
                label="Status" 
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Connector</InputLabel>
              <Select 
                label="Connector" 
                value={filters.connector}
                onChange={(e) => setFilters({ ...filters, connector: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Connectors</MenuItem>
                <MenuItem value="Type 2">Type 2</MenuItem>
                <MenuItem value="CCS2">CCS2</MenuItem>
                <MenuItem value="CHAdeMO">CHAdeMO</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={searchLoading}
              startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon fontSize="small" />}
              sx={{
                textTransform: 'none', fontWeight: 600, backgroundColor: '#536DFE', borderRadius: 2, px: 3, py: 1,
                '&:hover': { backgroundColor: '#3F51B5' }, '&:disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' }
              }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => { setFilters({ status: '', connector: '', searchTerm: '' }); setSearchTerm(''); setHasSearched(false); setSearchLoading(false); }}
              sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#536DFE', color: '#536DFE', borderRadius: 2, px: 3, py: 1,
                '&:hover': { borderColor: '#3F51B5', backgroundColor: '#f5f5f5' } }}
            >
              Clear All
            </Button>
          </Box>
        </CardContent>
      </Card>

      {hasSearched && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '1px solid #e8f5e8' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, backgroundColor: '#e8f5e8', borderRadius: 2, minWidth: 'fit-content' }}>
                {searchLoading ? (<CircularProgress size={20} color="primary" />) : (<SearchIcon sx={{ color: '#536DFE' }} />)}
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#536DFE' }}>Search Results</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">Searching charger types...</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                      {filteredData.length} type{filteredData.length !== 1 ? 's' : ''} found
                    </Typography>
                    {filters.status && (
                      <Chip label={`Status: ${filters.status}`} size="small" color="success" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                    )}
                    {filters.connector && (
                      <Chip label={`Connector: ${filters.connector}`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.75rem' }} />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Charger Types
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Type</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Connector</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Max Power</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Voltage/Current</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Brand/Model</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">Searching charger types...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow key={row.id} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' }, '&:hover': { backgroundColor: '#e8f5e8 !important' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: '#536DFE', width: 32, height: 32 }}>
                            <ChargerIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.typeName}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.description.substring(0, 40)}...</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={row.connectorType} size="small" sx={{ backgroundColor: '#536DFE', color: 'white', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.maxPowerKw} kW</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BoltIcon sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.voltageV} V / {row.currentA} A</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.brand} • {row.model}</TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={row.isActive}
                              onChange={() => handleStatusToggle(row)}
                              color="success"
                              size="medium"
                            />
                          }
                          label={<Chip label={row.isActive ? 'Active' : 'Inactive'} size="small" color={getStatusColor(row.isActive)} sx={{ fontWeight: 600 }} />}
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleView(row)} sx={{ color: '#536DFE' }}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Type">
                            <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: '#ff9800' }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">No charger types found matching your search criteria</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog 
        open={statusConfirmDialogOpen} 
        onClose={() => setStatusConfirmDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white', display: 'flex', alignItems: 'center', gap: 1, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {selectedForStatus?.isActive ? 'Deactivate Charger Type' : 'Activate Charger Type'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to {selectedForStatus?.isActive ? 'deactivate' : 'activate'} the charger type <strong>"{selectedForStatus?.typeName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedForStatus?.isActive
              ? 'Deactivating this type will mark it as unavailable for new assignments.'
              : 'Activating this type will make it available for selection again.'
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setStatusConfirmDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}>Cancel</Button>
          <Button onClick={confirmStatusToggle} variant="contained" color={selectedForStatus?.isActive ? 'warning' : 'success'} sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}>
            {selectedForStatus?.isActive ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#536DFE', color: 'white', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChargerIcon />
            <Typography variant="h6" fontWeight="bold">Charger Type Details</Typography>
          </Box>
          <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedCharger && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 64, height: 64 }}>
                  <ChargerIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">{selectedCharger.typeName}</Typography>
                  <Typography variant="body2" color="text.secondary">{selectedCharger.brand} • {selectedCharger.model}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip label={selectedCharger.isActive ? 'Active' : 'Inactive'} size="small" color={getStatusColor(selectedCharger.isActive)} />
                    <Chip label={`${selectedCharger.maxPowerKw} kW`} size="small" color="primary" />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PowerIcon color="primary" />
                        Electrical Specs
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Connector:</Typography>
                          <Chip label={selectedCharger.connectorType} size="small" sx={{ backgroundColor: '#536DFE', color: 'white' }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Max Power:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCharger.maxPowerKw} kW</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Voltage / Current:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCharger.voltageV} V / {selectedCharger.currentA} A</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Efficiency:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCharger.efficiency || 0}%</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BoltIcon color="primary" />
                        General
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Brand:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCharger.brand}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Model:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCharger.model}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Description:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedCharger.description}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button onClick={handleCloseViewDialog} variant="contained" sx={{ backgroundColor: '#536DFE', '&:hover': { backgroundColor: '#3F51B5' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: selectedCharger ? '#ff9800' : '#536DFE', color: 'white', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedCharger ? <Edit /> : <Add />}
            <Typography variant="h6" fontWeight="bold">{selectedCharger ? 'Edit Charger Type' : 'New Charger Type'}</Typography>
          </Box>
          <IconButton onClick={handleCloseEditDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {selectedCharger ? `Edit charger type: ${selectedCharger.typeName}` : 'Create new charger type'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Type Name" required value={editFormData.typeName} onChange={(e) => handleEditFormChange('typeName', e.target.value)} variant="outlined" size="small" error={!editFormData.typeName} helperText={!editFormData.typeName ? 'Type name is required' : ''} />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Connector Type</InputLabel>
                  <Select value={editFormData.connectorType} onChange={(e) => handleEditFormChange('connectorType', e.target.value)} label="Connector Type">
                    <MenuItem value="Type 2">Type 2</MenuItem>
                    <MenuItem value="CCS2">CCS2</MenuItem>
                    <MenuItem value="CHAdeMO">CHAdeMO</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Brand" value={editFormData.brand} onChange={(e) => handleEditFormChange('brand', e.target.value)} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Model" value={editFormData.model} onChange={(e) => handleEditFormChange('model', e.target.value)} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Max Power (kW)" type="number" required value={editFormData.maxPowerKw} onChange={(e) => handleEditFormChange('maxPowerKw', e.target.value)} variant="outlined" size="small" error={!editFormData.maxPowerKw} helperText={!editFormData.maxPowerKw ? 'Max power is required' : ''} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Voltage (V)" type="number" value={editFormData.voltageV} onChange={(e) => handleEditFormChange('voltageV', e.target.value)} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Current (A)" type="number" value={editFormData.currentA} onChange={(e) => handleEditFormChange('currentA', e.target.value)} variant="outlined" size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" value={editFormData.description} onChange={(e) => handleEditFormChange('description', e.target.value)} variant="outlined" size="small" multiline rows={2} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={editFormData.isActive} onChange={(e) => handleEditFormChange('isActive', e.target.checked)} color="success" />} label="Active Type" />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button onClick={handleCloseEditDialog} variant="outlined" sx={{ mr: 1 }}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" startIcon={<Save />} sx={{ backgroundColor: selectedCharger ? '#ff9800' : '#536DFE', '&:hover': { backgroundColor: selectedCharger ? '#f57c00' : '#3F51B5' } }}>
            {selectedCharger ? 'Save Changes' : 'Create Type'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChargerType;


